import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, SearchBar, Pagination, EmptyState, StatusBadge, useList, tableClass, theadClass, thClass, trClass, tdClass, tdFirstClass } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import TemplatePreview from "@/components/TemplatePreview";
import { FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminTemplates() {
  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters, refetch } = useList("/admin/templates");
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const open = (t) => { setSelected(t); setStatus(t.status); setReason(t.rejection_reason || ""); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/templates/${selected.id}/status`, { status, rejection_reason: reason });
      toast.success("Status template diperbarui.");
      setSelected(null); refetch();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Templates" subtitle="Semua template lintas user & ubah status manual." />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari nama template..." testid="admin-template-search" />
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 bg-white" data-testid="admin-template-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua status</SelectItem>{["DRAFT", "PENDING", "APPROVED", "REJECTED", "DISABLED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={FileText} title="Belum ada template" description="Template dari semua user akan muncul di sini." />
      ) : (
        <div className="bg-white rounded-lg overflow-hidden">
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr><th className={thClass}>Nama</th><th className={`${thClass} hidden md:table-cell`}>User</th><th className={`${thClass} hidden lg:table-cell`}>Channel</th><th className={`${thClass} hidden lg:table-cell`}>Kategori</th><th className={thClass}>Status</th></tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <tr key={t.id} onClick={() => open(t)} className={trClass} data-testid={`admin-template-row-${t.id}`}>
                  <td className={`${tdFirstClass} font-mono text-[13px] font-medium text-[#003399]`}>{t.name}</td>
                  <td className={`${tdClass} hidden md:table-cell`}>{t.user?.name || "-"}</td>
                  <td className={`${tdClass} hidden lg:table-cell`}>{t.channel_name || "-"}</td>
                  <td className={`${tdClass} hidden lg:table-cell`}>{t.category || "-"}</td>
                  <td className={tdClass}><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={!!selected} onClose={() => setSelected(null)} width="max-w-2xl" title={selected ? selected.name : ""} description="Ubah status template (management internal, bukan submit ke provider)." testid="admin-template-drawer"
        footer={<><Button variant="outline" onClick={() => setSelected(null)}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-[#003399] hover:bg-[#002B80]" data-testid="admin-template-save-status">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Status"}</Button></>}>
        {selected && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-neutral-500">User</p><p className="font-medium">{selected.user?.name}</p></div>
                <div><p className="text-xs text-neutral-500">Channel</p><p className="font-medium">{selected.channel_name}</p></div>
                <div><p className="text-xs text-neutral-500">Bahasa</p><p className="font-medium">{selected.language}</p></div>
                <div><p className="text-xs text-neutral-500">Kategori</p><p className="font-medium">{selected.category || "-"}</p></div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="admin-template-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["DRAFT", "PENDING", "APPROVED", "REJECTED", "DISABLED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {status === "REJECTED" && (
                <div className="space-y-2">
                  <Label>Alasan Penolakan</Label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-template-reason" placeholder="Alasan user-friendly..." />
                </div>
              )}
            </div>
            <div><TemplatePreview components={selected.components || []} /></div>
          </div>
        )}
      </RightDrawer>
    </div>
  );
}
